import { RRule } from "rrule";

export type AvType = "single" | "range" | "recurring";

export type AvRow = {
  id?: string;
  listing_id: string;
  type: AvType;
  start_at: string; // ISO
  end_at: string;   // ISO
  rrule?: string | null;
};

// Output-interval
export type Interval = { start: Date; end: Date };

// Udvid raw availability-rows til konkrete tidsvinduer i et datointerval
export function expandAvailability(
  rows: AvRow[],
  from: Date,
  to: Date
): Interval[] {
  const out: Interval[] = [];
  for (const r of rows) {
    const start = new Date(r.start_at);
    const end = new Date(r.end_at);
    const durationMs = end.getTime() - start.getTime();

    if (r.type === "single") {
      if (end >= from && start <= to) out.push({ start, end });
      continue;
    }

    if (r.type === "range" && !r.rrule) {
      // Ét langt spænd
      const s = start < from ? from : start;
      const e = end > to ? to : end;
      if (e > s) out.push({ start: s, end: e });
      continue;
    }

    if (r.type === "recurring" && r.rrule) {
      const rule = RRule.fromString(r.rrule);
      // Sørg for at rrule får dtstart som r.start_at (inkl. klokkeslæt)
      (rule as any).options.dtstart = start;

      const occurrences = rule.between(from, to, true);
      for (const occ of occurrences) {
        const occEnd = new Date(occ.getTime() + durationMs);
        out.push({ start: occ, end: occEnd });
      }
    }
  }

  // (valgfrit) Merge overlappende windows
  return mergeIntervals(out);
}

function mergeIntervals(intervals: Interval[]): Interval[] {
  if (intervals.length === 0) return [];
  const sorted = intervals.sort((a, b) => a.start.getTime() - b.start.getTime());
  const res: Interval[] = [sorted[0]];
  for (let i = 1; i < sorted.length; i++) {
    const last = res[res.length - 1];
    const cur = sorted[i];
    if (cur.start <= last.end) {
      if (cur.end > last.end) last.end = cur.end;
    } else {
      res.push({ ...cur });
    }
  }
  return res;
}

// Er et helt dags-dato (00:00–23:59) “berørt” af et eller flere availability-vinduer?
export function dayHasAvailability(d: Date, windows: Interval[]): boolean {
  const dayStart = new Date(d);
  dayStart.setHours(0, 0, 0, 0);
  const dayEnd = new Date(d);
  dayEnd.setHours(23, 59, 59, 999);
  return windows.some(w => w.end >= dayStart && w.start <= dayEnd);
}
